"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { verifyEmancipationToken, setEmancipationPassword } from "@/sous-profile/redux/thunks/sousProfileThunks";
import "./EmancipationRedirect.css";

/**
 * EmancipationRedirect / EmancipationActivation
 * Affiche directement la page d'activation via /emancipation?token=...
 */
export default function EmancipationRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const token = searchParams.get("token");

  const [status, setStatus] = useState("verifying"); // verifying, verified, setting-password, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileName, setProfileName] = useState("");

  // Disable scroll on mount
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage(t("emancipation.linkExpired"));
        return;
      }

      try {
        const result = await dispatch(verifyEmancipationToken(token)).unwrap();
        setProfileName(result?.profileName || "Your profile");
        setStatus("verified");
      } catch (error) {
        setStatus("error");
        setErrorMessage(error || t("emancipation.linkExpired"));
      }
    };

    verifyToken();
  }, [token, dispatch, t]);

  const handleSetPassword = async (e) => {
    e.preventDefault();

    // Validations
    if (!password) {
      message.error(t("emancipation.messages.emptyPassword"));
      return;
    }

    if (password.length < 8) {
      message.error(t("emancipation.messages.shortPassword"));
      return;
    }

    if (password !== confirmPassword) {
      message.error(t("emancipation.messages.passwordMismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        setEmancipationPassword({
          token,
          password,
        })
      ).unwrap();

      setStatus("success");
      message.success(t("emancipation.messages.success"));

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setIsSubmitting(false);
      message.error(error || t("emancipation.messages.error"));
    }
  };

  // Requirement checks
  const requirements = {
    length: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*]/.test(password),
    matches: password && confirmPassword && password === confirmPassword,
  };

  const allRequirementsMet = Object.values(requirements).every(Boolean);

  if (status === "verifying") {
    return (
      <div className="emancipation-container">
        <div className="emancipation-card">
          <div className="emancipation-loader">
            <div className="spinner"></div>
            <p>{t("emancipation.verifying")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="emancipation-container">
        <div className="emancipation-card error">
          <div className="error-icon">❌</div>
          <h2>{t("emancipation.invalidLink")}</h2>
          <p>{errorMessage}</p>
          <button
            className="emancipation-btn-primary"
            onClick={() => navigate("/login")}
          >
            {t("emancipation.returnToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="emancipation-container">
        <div className="emancipation-card success">
          <div className="success-icon">✅</div>
          <h2>{t("emancipation.success")}</h2>
          <p>{t("emancipation.successMessage")}</p>
          <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "10px" }}>
            {t("emancipation.redirecting")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="emancipation-container">
      <div className="emancipation-card">
        <form onSubmit={handleSetPassword} className="emancipation-form">
          {/* Password Input */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              NEW PASSWORD
            </label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {/* Confirm Password Input */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              CONFIRM PASSWORD
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-input"
            />
          </div>

          {/* Security Strength */}
          <div className="security-strength-box">
            <p className="security-strength-title">SECURITY STRENGTH</p>
            
            {/* Progress bar */}
            <div className="strength-bar-container">
              <div className="strength-bar-background">
                <div 
                  className="strength-bar-fill" 
                  style={{ width: `${(Object.values(requirements).filter(Boolean).length / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* Requirements grid */}
            <div className="requirements-grid">
              <div className={`requirement-item ${requirements.length ? "met" : ""}`}>
                <span className="requirement-check">{requirements.length ? "✓" : "○"}</span>
                <span className="requirement-text">8+ characters</span>
              </div>
              <div className={`requirement-item ${requirements.hasNumber ? "met" : ""}`}>
                <span className="requirement-check">{requirements.hasNumber ? "✓" : "○"}</span>
                <span className="requirement-text">One number</span>
              </div>
              <div className={`requirement-item ${requirements.hasSpecial ? "met" : ""}`}>
                <span className="requirement-check">{requirements.hasSpecial ? "✓" : "○"}</span>
                <span className="requirement-text">Special char</span>
              </div>
              <div className={`requirement-item ${requirements.hasUpperCase && requirements.hasLowerCase ? "met" : ""}`}>
                <span className="requirement-check">{requirements.hasUpperCase && requirements.hasLowerCase ? "✓" : "○"}</span>
                <span className="requirement-text">Mixed case</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="emancipation-btn-primary large-btn"
            disabled={!allRequirementsMet || isSubmitting}
          >
            {isSubmitting ? "Initializing..." : "Initialize Secure Account"}
          </button>

          {/* Disclaimer */}
          <p className="emancipation-disclaimer">
            By setting a password, you agree to our <a href="#privacy">Privacy Policy</a> regarding independent patient access.
          </p>
        </form>
      </div>
    </div>
  );
}
